import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BookOpenCheck, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

/**
 * Renders a "Import from library" trigger button. Click opens a dialog that
 * lists Show Library templates. Selecting one + Import calls the backend and
 * reports back how many characters/scenes/songs landed in the production.
 *
 * Props:
 *   - onImported(): callback so the parent can reload its list.
 *   - productionId: current production id (required).
 */
export default function ImportShowLibraryButton({ onImported, productionId }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [strategy, setStrategy] = useState("append");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || templates.length) return;
    setLoading(true);
    api.get("/show-library")
      .then((r) => setTemplates(r.data))
      .catch(() => toast.error("Couldn't load show library"))
      .finally(() => setLoading(false));
  }, [open, templates.length]);

  const selected = templates.find((t) => t.id === selectedId);

  const run = async () => {
    if (!selectedId || !productionId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/productions/${productionId}/import-template`, {
        template_id: selectedId,
        strategy,
      });
      const bits = [];
      if (data.imported.characters) bits.push(`${data.imported.characters} characters`);
      if (data.imported.scenes) bits.push(`${data.imported.scenes} scenes`);
      if (data.imported.songs) bits.push(`${data.imported.songs} songs`);
      toast.success(bits.length ? `Imported ${bits.join(", ")} from ${data.template.title}` : `Nothing new to import from ${data.template.title}`);
      setOpen(false);
      setSelectedId("");
      onImported && onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="open-import-library-btn"
        onClick={() => setOpen(true)}
        className="h-9 px-3.5 rounded border border-gold/40 text-gold hover:bg-gold/10 inline-flex items-center gap-1.5 text-sm font-medium"
      >
        <BookOpenCheck className="w-4 h-4" /> Import from library
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Import from show library</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Show</Label>
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger data-testid="import-template-select" className="mt-2 bg-[#121212] border-white/10 h-11">
                  <SelectValue placeholder={loading ? "Loading library…" : "Pick a show"} />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-72">
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <div className="flex flex-col text-left">
                        <span>{t.title}</span>
                        <span className="text-[10px] text-zinc-500">{t.character_count} chars · {t.scene_count} scenes · {t.song_count} songs</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selected?.description && (
                <p className="mt-2 text-xs text-zinc-500 leading-relaxed">{selected.description}</p>
              )}
            </div>

            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">If a name already exists in this production…</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  data-testid="strategy-append"
                  onClick={() => setStrategy("append")}
                  className={`px-3 py-2 rounded border text-xs text-left ${strategy === "append" ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-zinc-400 hover:border-white/20"}`}
                >
                  <div className="font-semibold text-sm">Skip duplicates</div>
                  <div className="opacity-80">Keep what&apos;s here, add what&apos;s new.</div>
                </button>
                <button
                  data-testid="strategy-replace"
                  onClick={() => setStrategy("replace")}
                  className={`px-3 py-2 rounded border text-xs text-left ${strategy === "replace" ? "border-red-500 bg-red-500/10 text-red-400" : "border-white/10 text-zinc-400 hover:border-white/20"}`}
                >
                  <div className="font-semibold text-sm">Replace all</div>
                  <div className="opacity-80">Wipe existing scenes first.</div>
                </button>
              </div>
              {strategy === "replace" && (
                <p className="mt-2 text-[11px] text-red-400">Cast members already tied to characters are preserved; only unassigned characters and all scenes get wiped.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="h-9 px-3 rounded border border-white/10 text-sm text-zinc-300 hover:bg-white/5">Cancel</button>
            <button data-testid="run-import-btn" onClick={run} disabled={!selectedId || busy} style={goldBtn} className="h-9 px-4 rounded font-semibold text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpenCheck className="w-4 h-4" />}
              {busy ? "Importing…" : "Import"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
